import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Empty,
  Segmented,
  Select,
  Spin,
  Tooltip,
  theme,
} from 'antd';
import {
  ArrowRightOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import {
  errorMessage,
  fetchNews,
  startDiscovery,
  waitForJob,
  fetchCapabilities,
  fetchRecentRuns,
  fetchJob,
  startCorpus,
} from './api';
import {
  DiscoveryResult,
  Instrument,
  NewsCuration,
  NewsResult,
  Period,
  EvidenceMode,
  NewsSource,
  SourceCapabilities,
  ViewRange,
  RecentRun,
  ReviewZone,
  ReviewNews,
  NewsArticle,
} from './types';
import EvidencePanel from './EvidencePanel';
import InstrumentSelect from './InstrumentSelect';
import PriceChart from './PriceChart';
import ReviewZones from './ReviewZones';
import { readSavedView, saveView, SavedView } from './viewState';
import PeriodList, {
  filterPeriods,
  initialPeriodFilters,
  PeriodFilters,
} from './PeriodList';
import './styles.css';
import './workbench.css';

type NewsState = { data?: NewsResult; loading: boolean; error?: string };

const MarketExplorer: React.FC = () => {
  const { token } = theme.useToken();
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [dateMode, setDateMode] = useState('All history');
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [runId, setRunId] = useState('');
  const [result, setResult] = useState<DiscoveryResult | null>(null);
  const [selected, setSelected] = useState<Period | null>(null);
  const [previewPeriod, setPreviewPeriod] = useState<Period | null>(null);
  const [news, setNews] = useState<Record<string, NewsState>>({});
  const [filters, setFilters] = useState<PeriodFilters>(initialPeriodFilters);
  const [periodLimit, setPeriodLimit] = useState(10);
  const [curationVersion, setCurationVersion] = useState(0);
  const [editingNews, setEditingNews] = useState(false);
  const [mode, setMode] = useState<EvidenceMode>('period');
  const [source, setSource] = useState<NewsSource>('pm');
  const [capabilities, setCapabilities] = useState<SourceCapabilities | null>(
    null,
  );
  const [viewport, setViewport] = useState<ViewRange | null>(null);
  const [viewEpoch, setViewEpoch] = useState(0);
  const [initialRange, setInitialRange] = useState<ViewRange | null>(null);
  const [recentRuns, setRecentRuns] = useState<RecentRun[]>([]);
  const [activeZone, setActiveZone] = useState<ReviewZone | null>(null);
  const [selectedReviewNews, setSelectedReviewNews] = useState<ReviewNews[]>(
    [],
  );
  const savedView = useRef(readSavedView());
  const analysis = useRef<AbortController | null>(null);
  const newsRequests = useRef(new Map<string, AbortController>());
  const newsCache = useRef<Record<string, NewsResult>>({});
  const newsCacheTimes = useRef<Record<string, number>>({});
  const previewTimer = useRef<number | null>(null);
  const evidenceRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => () => {
      analysis.current?.abort();
      newsRequests.current.forEach(controller => controller.abort());
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
    },
    [],
  );

  const restore = useCallback(
    async (id: string, view?: SavedView | null, zone?: ReviewZone) => {
      analysis.current?.abort();
      newsRequests.current.forEach(controller => controller.abort());
      newsRequests.current.clear();
      const controller = new AbortController();
      analysis.current = controller;
      setBusy(true);
      setError('');
      try {
        const initial = await fetchJob<DiscoveryResult>(id, controller.signal);
        const job = await waitForJob(initial, controller.signal);
        if (controller.signal.aborted) return;
        const periodId = zone?.source_period_id || view?.periodId;
        const period =
          job.result.periods.find(item => item.id === periodId) || null;
        const bounds = zone
          ? { min: Date.parse(zone.start_date), max: Date.parse(zone.end_date) }
          : view?.viewport || null;
        setViewEpoch(value => value + 1);
        setInitialRange(bounds);
        setViewport(bounds);
        setRunId(job.id);
        setResult(job.result);
        setInstrument(job.result.instrument);
        setSelected(period);
        setPreviewPeriod(null);
        setFilters(view?.filters || initialPeriodFilters);
        setPeriodLimit(10);
        setNews({});
        newsCache.current = {};
        setSource('pm');
        setMode('period');
        setSelectedReviewNews(zone?.news || []);
        setActiveZone(zone || null);
        const originalRequest = job.request || job.request_payload;
        const custom = Boolean(
          originalRequest?.start_date || originalRequest?.end_date,
        );
        setDateMode(custom ? 'Custom dates' : 'All history');
        setDates(
          custom
            ? [
                dayjs(
                  originalRequest?.start_date || job.result.range.start_date,
                ),
                dayjs(originalRequest?.end_date || job.result.range.end_date),
              ]
            : null,
        );
      } catch (reason) {
        if (!controller.signal.aborted)
          setError(
            errorMessage(reason) || 'This saved analysis could not be opened.',
          );
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchCapabilities(controller.signal)
      .then(value => {
        if (!controller.signal.aborted) setCapabilities(value);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setCapabilities({
            pm: { available: true },
            corpus: {
              enabled: false,
              available: false,
              message:
                'Local NewsRag is unavailable. PM news remains available.',
            },
          });
      });
    const view = savedView.current;
    if (view) void restore(view.runId, view);
    return () => controller.abort();
  }, [restore]);
  useEffect(() => {
    const controller = new AbortController();
    fetchRecentRuns(controller.signal, instrument?.id)
      .then(value => {
        if (!controller.signal.aborted) setRecentRuns(value.results);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [instrument?.id, runId]);
  useEffect(() => {
    if (runId && result && !busy)
      saveView({ runId, periodId: selected?.id, filters, viewport });
  }, [runId, result, selected?.id, filters, viewport, busy]);

  const analyze = async () => {
    if (
      !instrument ||
      editingNews ||
      (dateMode === 'Custom dates' && (!dates?.[0] || !dates?.[1]))
    )
      return;
    analysis.current?.abort();
    newsRequests.current.forEach(controller => controller.abort());
    newsRequests.current.clear();
    const controller = new AbortController();
    analysis.current = controller;
    setBusy(true);
    setError('');
    setPreviewPeriod(null);
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    try {
      const initial = await startDiscovery(
        {
          instrument_id: instrument.id,
          ...(dateMode === 'Custom dates' && dates?.[0] && dates?.[1]
            ? {
                start_date: dates[0].format('YYYY-MM-DD'),
                end_date: dates[1].format('YYYY-MM-DD'),
              }
            : {}),
        },
        controller.signal,
      );
      const job = await waitForJob(initial, controller.signal);
      if (controller.signal.aborted) return;
      setInitialRange(null);
      setViewport(null);
      setSelectedReviewNews([]);
      setActiveZone(null);
      setSource('pm');
      setMode('period');
      setRunId(job.id);
      setResult(job.result);
      setSelected(null);
      setFilters(initialPeriodFilters);
      setPeriodLimit(10);
      setNews({});
      newsCache.current = {};
    } catch (reason) {
      if (!controller.signal.aborted)
        setError(
          errorMessage(reason) ||
            'The analysis could not be completed. Please try again.',
        );
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  const newsKey = useCallback(
    (
      periodId: string,
      chosenSource: NewsSource = source,
      chosenMode: EvidenceMode = mode,
    ) => `${runId}:${periodId}:${chosenSource}:${chosenMode}`,
    [runId, source, mode],
  );
  const loadNews = useCallback(
    async (period: Period, retry = false, explicitCorpus = false) => {
      const key = newsKey(period.id);
      if (
        !runId ||
        newsRequests.current.has(key) ||
        (!retry &&
          newsCache.current[key] &&
          Date.now() - (newsCacheTimes.current[key] || 0) < 60000) ||
        (source === 'corpus' && !explicitCorpus)
      )
        return;
      const controller = new AbortController();
      newsRequests.current.set(key, controller);
      setNews(current => ({ ...current, [key]: { loading: true } }));
      try {
        let data: NewsResult;
        if (source === 'corpus') {
          const initial = await startCorpus(
            runId,
            period.id,
            mode,
            controller.signal,
          );
          const job = await waitForJob(initial, controller.signal);
          data = { ...job.result, source_job_id: job.id };
        } else
          data = await fetchNews(runId, period.id, controller.signal, mode);
        if (controller.signal.aborted) return;
        newsCache.current[key] = data;
        newsCacheTimes.current[key] = Date.now();
        setNews(current => ({ ...current, [key]: { loading: false, data } }));
      } catch (reason) {
        if (!controller.signal.aborted)
          setNews(current => ({
            ...current,
            [key]: {
              loading: false,
              error:
                errorMessage(reason) || 'Related news could not be loaded.',
            },
          }));
      } finally {
        if (newsRequests.current.get(key) === controller)
          newsRequests.current.delete(key);
      }
    },
    [runId, source, mode, newsKey],
  );

  useEffect(() => {
    if (selected && source === 'pm') void loadNews(selected);
  }, [selected, source, mode, loadNews]);

  const selectPeriod = useCallback(
    (period: Period) => {
      if (editingNews) return;
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
      if (period.id !== selected?.id) {
        setSelectedReviewNews([]);
        setActiveZone(null);
      }
      setSelected({ ...period });
      setPreviewPeriod(null);
      void loadNews(period);
      if (window.innerWidth < 960)
        window.setTimeout(
          () =>
            evidenceRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            }),
          0,
        );
    },
    [loadNews, editingNews, selected?.id],
  );

  const preview = useCallback((period: Period) => {
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    setPreviewPeriod(period);
    // Hover is instant and local. Selecting a period expresses news intent.
  }, []);

  const endPreview = useCallback(() => {
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    setPreviewPeriod(null);
  }, []);

  const selectionRef = useRef(selected);
  const newsLoaderRef = useRef(loadNews);
  selectionRef.current = selected;
  newsLoaderRef.current = loadNews;

  const newsChanged = useCallback((changed: NewsCuration) => {
    const activePeriod = selectionRef.current;
    const reloadSelection = Boolean(
      activePeriod &&
        Array.from(newsRequests.current.keys()).some(key =>
          key.includes(`:${activePeriod.id}:`),
        ),
    );
    newsRequests.current.forEach(controller => controller.abort());
    newsRequests.current.clear();
    newsCache.current = {};
    setCurationVersion(version => version + 1);
    setNews(current =>
      Object.fromEntries(
        Object.entries(current)
          .filter(([id]) => !id.includes(':corpus:'))
          .map(([id, state]) => [
            id,
            {
              ...state,
              ...(state.data
                ? {
                    data: {
                      ...state.data,
                      news: state.data.news.map(item =>
                        String(item.id) === String(changed.id)
                          ? {
                              ...item,
                              headline: changed.headline,
                              body: changed.body,
                              excerpt: changed.body.slice(0, 700),
                              tags: changed.tags,
                              curation_revision: changed.revision,
                            }
                          : item,
                      ),
                    },
                  }
                : {}),
            },
          ]),
      ),
    );
    if (activePeriod && reloadSelection)
      void newsLoaderRef.current(activePeriod, true);
  }, []);

  const selectedNews = selected ? news[newsKey(selected.id)] : undefined;
  const previewNews = previewPeriod
    ? news[newsKey(previewPeriod.id)]
    : undefined;
  const warnings = result?.quality?.warnings || [];
  const periods = result?.periods || [];
  const filteredPeriods = useMemo(
    () => filterPeriods(result?.periods || [], filters, viewport),
    [result, filters, viewport],
  );

  const toggleReviewNews = (article: NewsArticle) =>
    setSelectedReviewNews(current =>
      current.some(link => String(link.news_id) === String(article.id))
        ? current.filter(link => String(link.news_id) !== String(article.id))
        : [
            ...current,
            {
              news_id: article.id,
              headline: article.headline,
              curation_revision: article.curation_revision,
              labels: [],
              judgment: 'unreviewed',
              ...(source === 'corpus' && selectedNews?.data?.source_job_id
                ? { evidence_job_id: selectedNews.data.source_job_id }
                : {}),
            },
          ],
    );

  return (
    <div
      className="market-explorer"
      style={
        {
          '--me-bg': token.colorBgContainer,
          '--me-text': token.colorText,
          '--me-muted': token.colorTextSecondary,
          '--me-border': token.colorBorderSecondary,
          '--me-primary': token.colorPrimary,
          '--me-soft': token.colorFillAlter,
        } as React.CSSProperties
      }
    >
      <header className="market-explorer__page-heading">
        <div>
          <div className="market-explorer__eyebrow">MARKET RESEARCH</div>
          <h1>Market Explorer</h1>
          <p>Find notable price moves. Explore the news around them.</p>
        </div>
        <Tooltip title="Explore stored historical prices and evidence around unusual periods. Analysis is retrospective; related news is not proof of causation.">
          <button
            className="market-explorer__help"
            type="button"
            aria-label="About Market Explorer"
          >
            <InfoCircleOutlined /> How it works
          </button>
        </Tooltip>
      </header>

      {recentRuns.length > 0 && (
        <div className="market-explorer__recent-runs">
          <span>Resume research</span>
          <Select
            aria-label="Recent analyses"
            placeholder="Open a previous analysis"
            value={undefined}
            showSearch
            optionFilterProp="label"
            disabled={busy || editingNews}
            options={recentRuns.map(run => ({
              value: run.id,
              label: `${run.label} · ${new Date(run.created_at).toLocaleDateString()} · ${run.status.toLowerCase()}`,
            }))}
            onChange={id => void restore(id)}
          />
        </div>
      )}
      <section
        className="market-explorer__controls"
        aria-label="Analysis controls"
      >
        <div className="market-explorer__instrument-field">
          <label htmlFor="market-explorer-instrument">
            Instrument{' '}
            <Tooltip title="Search by ticker or instrument name. Only available historical observations are used.">
              <InfoCircleOutlined tabIndex={0} />
            </Tooltip>
          </label>
          <InstrumentSelect
            value={instrument}
            onChange={setInstrument}
            disabled={busy || editingNews}
          />
        </div>
        <div className="market-explorer__range-field">
          <label>
            Period{' '}
            <Tooltip title="All history uses the complete stored price range. Custom dates restrict the search for interesting periods.">
              <InfoCircleOutlined tabIndex={0} />
            </Tooltip>
          </label>
          <Segmented
            aria-label="Analysis date range"
            value={dateMode}
            onChange={value => setDateMode(String(value))}
            options={['All history', 'Custom dates']}
            size="large"
            disabled={busy || editingNews}
          />
        </div>
        {dateMode === 'Custom dates' && (
          <div className="market-explorer__date-field">
            <label>Choose dates</label>
            <DatePicker.RangePicker
              aria-label="Custom analysis dates"
              value={dates}
              onChange={value => setDates(value)}
              size="large"
              allowClear
              disabled={busy || editingNews}
            />
          </div>
        )}
        <Tooltip title="Find unusual moves, sustained trends and changes in volatility using the stored price history. AI explanations are optional and run separately.">
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            loading={busy}
            onClick={analyze}
            disabled={
              !instrument ||
              editingNews ||
              (dateMode === 'Custom dates' && (!dates?.[0] || !dates?.[1]))
            }
          >
            Analyze
          </Button>
        </Tooltip>
      </section>
      {error && (
        <Alert
          type="error"
          showIcon
          title="Analysis unavailable"
          description={error}
          closable
          onClose={() => setError('')}
        />
      )}
      {busy && (
        <div className="market-explorer__progress" role="status">
          <Spin size="small" />
          <span>
            Analyzing stored price history…
            <small>
              Finding unusual moves and changes in market conditions.
            </small>
          </span>
        </div>
      )}

      {!result && !busy && (
        <section className="market-explorer__welcome">
          <div className="market-explorer__welcome-icon">
            <LineChartOutlined />
          </div>
          <h2>A clearer view of the moments that mattered</h2>
          <p>
            Start with an instrument. We’ll surface interesting periods
            <br className="market-explorer__desktop-break" /> and help you
            examine their news, one story at a time.
          </p>
          <div className="market-explorer__steps">
            <span>
              <b>1</b> Choose an instrument
            </span>
            <ArrowRightOutlined />
            <span>
              <b>2</b> Find notable periods
            </span>
            <ArrowRightOutlined />
            <span>
              <b>3</b> Explore the evidence
            </span>
          </div>
        </section>
      )}

      {result && (
        <div
          className={
            busy
              ? 'market-explorer__results is-updating'
              : 'market-explorer__results'
          }
          aria-busy={busy}
        >
          {result.points.length > 0 ? (
            <PriceChart
              key={`${runId}:${viewEpoch}`}
              runId={runId}
              result={result}
              initialRange={initialRange}
              onRangeChange={setViewport}
              disabled={editingNews}
              periods={filteredPeriods}
              selected={selected}
              onSelect={selectPeriod}
              onPreview={preview}
              onPreviewEnd={endPreview}
              preview={
                previewPeriod
                  ? {
                      period: previewPeriod,
                      news: previewNews?.data,
                      error: previewNews?.error,
                      loading: previewNews?.loading ?? false,
                    }
                  : null
              }
            />
          ) : (
            <Empty description="No usable price observations were found for this range." />
          )}
          {warnings.length > 0 && (
            <div className="market-explorer__quality-notice">
              <InfoCircleOutlined />
              <span>
                {warnings[0]}
                {warnings.length > 1
                  ? ` ${warnings.length - 1} more coverage notes below.`
                  : ''}
              </span>
            </div>
          )}
          <ReviewZones
            instrument={result.instrument}
            runId={runId}
            period={selected}
            selectedNews={selectedReviewNews}
            activeZone={activeZone}
            disabled={busy || editingNews}
            onRestore={zone => void restore(zone.source_run_id, null, zone)}
            onSaved={zone => {
              if (
                zone.source_run_id === runId &&
                zone.source_period_id === selected?.id
              ) {
                setSelectedReviewNews(zone.news || []);
                setActiveZone(zone);
              }
            }}
            onArchived={id => {
              if (activeZone?.id === id) setActiveZone(null);
            }}
          />
          <div className="market-explorer__exploration-grid">
            <PeriodList
              periods={periods}
              filtered={filteredPeriods}
              selected={selected}
              filters={filters}
              viewport={viewport}
              limit={periodLimit}
              editing={editingNews}
              onFilters={value => {
                setFilters(value);
                setPeriodLimit(10);
              }}
              onLimit={setPeriodLimit}
              onSelect={selectPeriod}
            />
            <div ref={evidenceRef} className="market-explorer__evidence-wrap">
              {selected ? (
                <EvidencePanel
                  runId={runId}
                  period={selected}
                  news={selectedNews?.data}
                  loading={selectedNews?.loading ?? source === 'pm'}
                  error={selectedNews?.error}
                  onRetry={() =>
                    void loadNews(selected, true, source === 'corpus')
                  }
                  mode={mode}
                  source={source}
                  capabilities={capabilities}
                  onModeChange={setMode}
                  onSourceChange={setSource}
                  onSearchCorpus={() => void loadNews(selected, true, true)}
                  selectedReviewNews={selectedReviewNews}
                  onReviewToggle={toggleReviewNews}
                  instrument={result.instrument}
                  curationVersion={curationVersion}
                  onNewsChanged={newsChanged}
                  onEditingChange={setEditingNews}
                />
              ) : (
                <section className="market-explorer__evidence-placeholder">
                  <LineChartOutlined />
                  <h2>Follow a move. Find its story.</h2>
                  <p>
                    Choose a numbered marker or a period to see
                    <br className="market-explorer__desktop-break" /> the
                    related news and explore possible drivers.
                  </p>
                </section>
              )}
            </div>
          </div>
          <Collapse
            className="market-explorer__methodology"
            ghost
            size="small"
            items={[
              {
                key: 'methodology',
                label: 'Method & data coverage',
                children: (
                  <div>
                    <p>
                      {result.methodology?.summary ||
                        'Periods are detected from stored prices, then matched with related historical news. Results describe the past and do not establish causation.'}
                    </p>
                    <ul className="market-explorer__plain-list">
                      {[
                        ...warnings,
                        ...(typeof result.methodology?.limitations === 'string'
                          ? [result.methodology.limitations]
                          : result.methodology?.limitations || []),
                      ].map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default MarketExplorer;
